import { Material } from "../types";
import { PartTypeSchema, PartTypeType } from "../types/PrismaZod";

export type FormatterMaterialParameter = {
  width?: number | null;
  height?: number | null;
  length?: number | null;
  tubeThickness?: number | null;
};

export type FieldConfig = {
  key: "thickness" | "diameter" | "height" | "width" | "length";
  field: keyof FormatterMaterialParameter;
  symbol?: string;
  syncWith?: keyof FormatterMaterialParameter;
};
type DisplayConfig = { fields: FieldConfig[] };

const DEFAULT_DISPLAY_CONFIG: DisplayConfig = {
  fields: [
    { key: "height", field: "height" },
    { key: "width", field: "width" },
    { key: "length", field: "length" },
  ],
};

const SHEET_DISPLAY_CONFIG: DisplayConfig = {
  fields: [
    { key: "thickness", field: "height", symbol: "ep." },
    { key: "length", field: "length" },
    { key: "width", field: "width" },
  ],
};

const PART_DISPLAY_CONFIG: Record<PartTypeType, DisplayConfig> = {
  [PartTypeSchema.enum.SHEET]: SHEET_DISPLAY_CONFIG,
  [PartTypeSchema.enum.ASSEMBLY]: DEFAULT_DISPLAY_CONFIG,
  [PartTypeSchema.enum.CONSUMABLE]: DEFAULT_DISPLAY_CONFIG,
  [PartTypeSchema.enum.OTHER]: DEFAULT_DISPLAY_CONFIG,
  [PartTypeSchema.enum.MACHINING]: {
    fields: [
      { key: "height", field: "height" },
      { key: "width", field: "width" },
      { key: "length", field: "length" },
    ],
  },
  [PartTypeSchema.enum.ROUND_TUBE]: {
    fields: [
      { key: "thickness", field: "tubeThickness", symbol: "ep." },
      { key: "diameter", field: "width", symbol: "Ø", syncWith: "height" },
      { key: "length", field: "length" },
    ],
  },
  [PartTypeSchema.enum.ROUND_BAR]: {
    fields: [
      { key: "diameter", field: "width", symbol: "Ø", syncWith: "height" },
      { key: "length", field: "length" },
    ],
  },
  [PartTypeSchema.enum.SQUARE_TUBE]: {
    fields: [
      { key: "thickness", field: "tubeThickness", symbol: "ep." },
      { key: "height", field: "height" },
      { key: "width", field: "width" },
      { key: "length", field: "length" },
    ],
  },
  [PartTypeSchema.enum.SQUARE_BAR]: {
    fields: [
      { key: "height", field: "height" },
      { key: "width", field: "width" },
      { key: "length", field: "length" },
    ],
  },
  [PartTypeSchema.enum.RECTANGULAR_TUBE]: {
    fields: [
      { key: "thickness", field: "tubeThickness", symbol: "ep." },
      { key: "height", field: "height" },
      { key: "width", field: "width" },
      { key: "length", field: "length" },
    ],
  },
  [PartTypeSchema.enum.PROFILE]: {
    fields: [
      { key: "height", field: "height" },
      { key: "width", field: "width" },
      { key: "length", field: "length" },
    ],
  },
};

export const sortMaterialDimensions = <
  T extends Pick<Material, "height" | "width" | "length" | "tubeThickness">,
>(
  materials: T[],
  partType: PartTypeType | null,
): T[] => {
  const fields = getMaterialDimensionsFieldsConfiguration(partType);
  return materials.sort((a, b) => {
    for (const { field } of fields) {
      if (a[field] !== b[field]) {
        if (a[field] === null) {
          return 1;
        }
        if (b[field] === null) {
          return -1;
        }
        return a[field] - b[field];
      }
    }
    return 0;
  });
};

export type NormalizedPartDimensions = {
  length: number | null;
  width: number | null;
  height: number | null;
};

export const getNormalizedPartDimensions = (
  partType: PartTypeType | null | undefined,
  part: FormatterMaterialParameter,
): NormalizedPartDimensions => {
  const fields = getMaterialDimensionsFieldsConfiguration(partType);

  const findValue = (keys: FieldConfig["key"][]): number | null => {
    const config = fields.find((f) => keys.includes(f.key));
    return config ? (part[config.field] ?? null) : 0;
  };

  return {
    length: findValue(["length"]),
    width: findValue(["width"]),
    height: findValue(["thickness", "diameter", "height"]),
  };
};

export const getMaterialDimensionsFieldsConfiguration = (
  partType: PartTypeType | undefined | null,
): FieldConfig[] => {
  const displayConfig = partType ? PART_DISPLAY_CONFIG[partType] : undefined;
  return displayConfig ? displayConfig.fields : DEFAULT_DISPLAY_CONFIG.fields;
};

export const formatMaterialDimensions = (
  partType: PartTypeType | undefined | null,
  material: FormatterMaterialParameter,
) => {
  const fields = getMaterialDimensionsFieldsConfiguration(partType);

  const parts = fields
    .filter((f) => material[f.field] !== null && material[f.field] !== undefined)
    .map((f) => `${f.symbol ?? ""}${material[f.field] as number}`);

  return parts.length > 0 ? `${parts.join("x")} mm` : "";
};

export const getMaterialDimensionsFormatExplanation = (
  partType: PartTypeType | undefined,
  material: FormatterMaterialParameter,
): {
  thickness?: number | null;
  width?: number | null;
  length?: number | null;
  diameter?: number | null;
  height?: number | null;
} => {
  const result: {
    thickness?: number | null;
    width?: number | null;
    length?: number | null;
    diameter?: number | null;
    height?: number | null;
  } = {
    thickness: null,
    width: null,
    length: null,
    diameter: null,
    height: null,
  };

  const fields = getMaterialDimensionsFieldsConfiguration(partType);

  fields.forEach((field) => {
    const key = field.key;
    result[key] = material[field.field] ?? null;
  });

  return result;
};
